import {inject, Injectable} from '@angular/core';
import Keycloak from "keycloak-js";
import {KC_ID_TOKEN_KEY, KC_REFRESH_TOKEN_KEY, KC_TOKEN_KEY} from "../providers/keycloak.provider";
import {clearPersistedTokens, redirectToLogin} from "./login-redirect";

@Injectable({providedIn: 'root'})
export class TokenRefreshService {
  private keycloak = inject(Keycloak);
  private intervalId: ReturnType<typeof setInterval> | null = null;

  start(): void {
    // Persist tokens on auth events
    this.keycloak.onAuthSuccess = (): void => this.persistTokens();
    this.keycloak.onAuthRefreshSuccess = (): void => this.persistTokens();
    // Appelé par keycloak-js quand la session est perdue côté serveur : refresh refusé en 400
    // (« invalid_grant / Session not active » : sessions révoquées, compte désactivé…), pas sur une
    // simple coupure réseau. On renvoie vers la connexion au lieu de laisser une page périmée.
    this.keycloak.onAuthLogout = (): void => this.onSessionLost();

    // If already authenticated (tokens restored from storage), persist fresh tokens
    if (this.keycloak.authenticated) {
      this.persistTokens();
    }

    this.intervalId = setInterval(() => {
      if (this.keycloak.authenticated) {
        this.keycloak.updateToken(70).catch(() => {
          this.clearTokens();
        });
      }
    }, 60000);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  clearTokens(): void {
    clearPersistedTokens();
  }

  /** Session perdue : arrêt du rafraîchissement, purge des jetons, retour à la connexion (garde anti-boucle). */
  private onSessionLost(): void {
    this.stop();
    this.clearTokens();
    redirectToLogin(this.keycloak);
  }

  // Persistance « rester connecté » en localStorage (survit au rechargement, à la fermeture du
  // navigateur et aux nouveaux onglets). Le refresh token est désormais persisté pour permettre
  // la réémission d'un access token frais sans dépendre du check-sso silencieux (cookies tiers).
  // ⚠️ Compromis sécurité assumé (cf. keycloak.provider.ts) : surface d'exfiltration XSS élargie.
  private persistTokens(): void {
    if (this.keycloak.token) {
      localStorage.setItem(KC_TOKEN_KEY, this.keycloak.token);
    }
    if (this.keycloak.idToken) {
      localStorage.setItem(KC_ID_TOKEN_KEY, this.keycloak.idToken);
    }
    if (this.keycloak.refreshToken) {
      localStorage.setItem(KC_REFRESH_TOKEN_KEY, this.keycloak.refreshToken);
    }
  }
}
